import { cn } from "@/lib/utils";

/**
 * Curated Radium “product” models for the models page (pricing + detail cards).
 * Mirrors `radium-outcomes` `ModelsPage` + `data/models` — styling uses `--ds-*` tokens
 * (see `DataTable.module.css` + `ModelsPage.module.css` in outcomes).
 */
export interface RadiumShowcaseModel {
  id: string;
  marketingName: string;
  tagline: string;
  description: string;
  inputPerMTok: number;
  outputPerMTok: number;
  contextWindow: number;
  maxOutputTokens: number;
  equivalents: readonly string[];
}

export const RADIUM_SHOWCASE_MODELS = [
  {
    id: "hal-1.0",
    marketingName: "Hal 1.0",
    tagline: "Maximum capability",
    description:
      "Best for complex reasoning, nuanced writing, and workloads where maximum output quality matters more than raw speed.",
    inputPerMTok: 2.75,
    outputPerMTok: 13.75,
    contextWindow: 128_000,
    maxOutputTokens: 32_768,
    equivalents: ["Anthropic Opus 4.7", "OpenAI GPT-5.5"],
  },
  {
    id: "clarke-1.0",
    marketingName: "Clarke 1.0",
    tagline: "Balanced performance",
    description:
      "Best for assistants, product workflows, and applications that need strong quality with predictable latency and cost.",
    inputPerMTok: 1.5,
    outputPerMTok: 7,
    contextWindow: 200_000,
    maxOutputTokens: 16_384,
    equivalents: ["Anthropic Sonnet 4.6", "OpenAI GPT-5.4"],
  },
  {
    id: "tycho-1.0",
    marketingName: "Tycho 1.0",
    tagline: "High-efficiency scale",
    description:
      "Best for high-throughput workloads, lightweight assistants, and applications where latency and cost matter.",
    inputPerMTok: 0.5,
    outputPerMTok: 2.25,
    contextWindow: 128_000,
    maxOutputTokens: 8192,
    equivalents: ["Anthropic Haiku 4.5", "OpenAI GPT-5.4 Mini"],
  },
] as const satisfies readonly RadiumShowcaseModel[];

/** Detail card top accent — matches `ModelsPage.module.css` `.accentHal` / Clarke / Tycho. */
export const modelDetailCardTopAccentClass = (modelId: string) =>
  cn(
    "border-t-[3px] border-solid",
    modelId === "hal-1.0" && "border-t-[color:var(--ds-color-accent-primary)]",
    modelId === "clarke-1.0" && "border-t-[color:var(--ds-color-state-success)]",
    modelId === "tycho-1.0" && "border-t-[color:var(--ds-color-state-info)]",
  );

/** Equivalent-model pills — aligned with outcomes `MODEL_BADGE_VARIANT` (accent / success / info). */
export const modelEquivalentBadgeClass = (modelId: string) =>
  cn(
    "rounded-md border px-2.5 py-0.5 text-xs font-medium",
    modelId === "hal-1.0" &&
      "border-[color:var(--ds-color-border-default)] bg-[color:var(--ds-color-accent-subtle)] text-foreground",
    modelId === "clarke-1.0" &&
      "border-[color:var(--ds-color-state-success)]/40 bg-[color:var(--ds-color-state-success-subtle)] text-[color:var(--ds-color-state-success)]",
    modelId === "tycho-1.0" &&
      "border-[color:var(--ds-color-state-info)]/40 bg-[color:var(--ds-color-state-info-subtle)] text-[color:var(--ds-color-state-info)]",
  );
